import { DEFAULT_DIALOG_CONFIG } from '@angular/cdk/dialog';
import { HttpClient, provideHttpClient } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { getAnalytics, provideAnalytics } from '@angular/fire/analytics';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getRemoteConfig, provideRemoteConfig } from '@angular/fire/remote-config';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withRouterConfig } from '@angular/router';
import { provideIcons } from '@ng-icons/core';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { createErrorHandler, TraceService } from '@sentry/angular';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { LucideAngularModule } from 'lucide-angular';
import { provideToastr } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { ICONS, NGICONS } from 'src/icons';
import { DefaultDialogConfig } from 'src/shared/config/dialog.config';
import { registerFormioFileWithDownload } from 'src/shared/formio/formio-file-with-download';
import { registerFormioStepSlider } from 'src/shared/formio/formio-step-slider';
import { provideChunkLoadRecovery } from 'src/shared/routing/chunk-load-recovery';
import { provideAuthAppInitialiser } from 'src/shared/services/auth-state/auth-app-initialiser';
import { HttpLoaderFactory } from 'src/shared/translate/custom.translate.loader';
import { PortalRoutes } from './portal-routes';
import { Formio, FormioAppConfig } from '@formio/angular';
import premium from '@formio/premium';
registerFormioFileWithDownload();
Formio.license =
  'eyJhbGciOiJQUzI1NiJ9.eyJsaWNlbnNlTmFtZSI6IlZFVCBBSSBMaWJyYXJ5IExpY2Vuc2UgdGhyb3VnaCAwMS8xOC8yMDI2IiwiaWF0IjoxNzY3Njc1NjAwLCJleHAiOjE4MDAyOTE2MDAsImZhaWxEYXRlIjoxODAyOTcwMDAwLCJ0ZXJtcyI6eyJwcmVtaXVtIjp0cnVlLCJvZmZsaW5lIjp0cnVlLCJlbnRlcnByaXNlQnVpbGRlciI6ZmFsc2UsInJlcG9ydGluZyI6ZmFsc2UsInNxbGNvbm5lY3RvciI6ZmFsc2UsImVzaWduIjpmYWxzZSwiZW5kcG9pbnRzIjpbIioudmV0LWFpLmNvbSIsIiouam9paXBldGNhcmUuY29tIiwiKi52ZXJjZWwuYXBwIl0sImhvc3RuYW1lcyI6WyIqLnZldC1haS5jb20iLCIqLmpvaWlwZXRjYXJlLmNvbSIsIioudmVyY2VsLmFwcCJdfSwiaXNzIjoiaHR0cHM6Ly9mb3JtLmlvIiwic3ViIjoiRm9ybS5pbyJ9.lOmsFrwnzP3BO5_MJc68C2TXCPBVxS_xUOR8ztnhFf-ZYpbnnKQQvAvcfeAJs_ye9bEMZaPaBevB4xwKUesgPwjJ_ppdj1b1-y5auIIZpJ807IFwtUw7jvEMrMdJM8MFWwv2iXBiJLK6ANgOLBY5n7yHqM2NHyByE_0Hv-WWoInrDuwczFsuGOsFNaNkFisvx4jf2c3Y1McNuHIjgqC4dBLp1WG51DVMPsj78IIR2J5X4JKik7qQ-Vr8Lma-mvAO7ZIQpUX2HztxINSbjiiRdmheI0b5AAqP0_Bj80QA-K9P_rzFJFzJ2sKC4TkIGBPxoiliQ2WZwe5NmRn-poo63Q';
Formio.use(premium);
registerFormioStepSlider();

export const portalConfig: ApplicationConfig = {
  providers: [
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAnalytics(() => getAnalytics()),
    provideAuth(() => getAuth()),
    provideFirestore(() => getFirestore()),
    provideStorage(() => getStorage()),
    provideRemoteConfig(() => getRemoteConfig()),
    provideHttpClient(),
    provideAngularSvgIcon(),
    provideIcons(NGICONS),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimations(),
    provideToastr({ timeOut: 10000, positionClass: 'toast-bottom-center' }),
    importProvidersFrom(LucideAngularModule.pick(ICONS)),
    {
      provide: ErrorHandler,
      useValue: createErrorHandler({
        showDialog: false,
      }),
    },
    provideAppInitializer(() => {
      inject(TraceService);
    }),
    provideAuthAppInitialiser(),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      })
    ),
    provideRouter(
      PortalRoutes,
      withRouterConfig({
        onSameUrlNavigation: 'reload',
      })
    ),
    provideChunkLoadRecovery({ notFoundPath: 'notfound' }),
    { provide: DEFAULT_DIALOG_CONFIG, useValue: DefaultDialogConfig },
    {
      provide: FormioAppConfig,
      useValue: new FormioAppConfig({
        baseUrl: environment.formIoRootUrl,
        appUrl: `${environment.formIoRootUrl}/jxcochdpcfazlkz`,
      }),
    },
  ],
};
